import { LogUserActivity } from '@/apis/users/requests'
import { LOG_TYPES } from '@/common/constants.ts'
import { getInfoDashboard } from '@/queries/dashboard/queries'
import { RootState } from '@/storage/store'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import {
  InfoDashboardProps,
  InfoDashboardSchema,
} from '@/queries/dashboard/schemas'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { ConvertStringToDate, DatePicker } from '@/common/components/datepicker'
import FormErrorMessage from '@/common/components/FormErrorMessage'
import { Button } from '@/app/common/components/button/Button'
import { useEffect, useRef } from 'react'

type StateProp = {
  name: string
  stat?: string | number
  details?: StateProp[]
}

export default function DashboardPage() {
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<InfoDashboardProps>({
    resolver: zodResolver(InfoDashboardSchema),
  })
  const start_date = watch('start_date')
  const end_date = watch('end_date')

  const { data: infoDashbord, refetch } = useQuery({
    queryKey: ['dashboard', selectedProgramYear?.id],
    queryFn: () =>
      getInfoDashboard(selectedProgramYear?.id ?? 0, { start_date, end_date }),
    enabled: !!selectedProgramYear,
  })

  const onSubmit: SubmitHandler<InfoDashboardProps> = async () => {
    refetch()
  }

  const Stats: StateProp[] = [
    {
      name: 'New Applications Started',
      stat: infoDashbord?.new_application_started,
    },
    {
      name: 'Identity Applications Completed ',
      stat:
        infoDashbord?.new_application_steps &&
        infoDashbord?.new_application_steps.identity.clear +
          infoDashbord?.new_application_steps.identity.manual,
      details: [
        {
          name: 'Clear',
          stat:
            infoDashbord?.new_application_steps &&
            infoDashbord?.new_application_steps.identity.clear,
        },
        {
          name: 'Manual',
          stat:
            infoDashbord?.new_application_steps &&
            infoDashbord?.new_application_steps.identity.manual,
        },
        {
          name: 'Open Flags',
          stat:
            infoDashbord?.new_application_steps &&
            infoDashbord?.new_application_steps.identity.flags,
        },
      ],
    },
    {
      name: 'Residency Applications Completed ',
      stat:
        infoDashbord?.new_application_steps &&
        infoDashbord?.new_application_steps.residency.passed,
      details: [
        {
          name: 'Open Flags',
          stat:
            infoDashbord?.new_application_steps &&
            infoDashbord?.new_application_steps.residency.flags,
        },
      ],
    },
    { name: 'New Applicants Details', stat: 'Not Applicable' },
    {
      name: 'Income Applicants Completed ',
      stat:
        infoDashbord?.new_application_steps &&
        infoDashbord?.new_application_steps.income.dws +
          infoDashbord.new_application_steps.income.tax_commission +
          infoDashbord?.new_application_steps.income.manual,
    },
    {
      name: 'New Applications Completed',
      stat: infoDashbord?.new_application_completed,
      details: [
        {
          name: 'In Progress',
          stat: infoDashbord?.new_application_in_progress,
        },
        { name: 'Withdrawn', stat: infoDashbord?.new_application_withdrawn },
      ],
    },
    { name: 'New Student Started', stat: infoDashbord?.new_student_started },
    { name: 'New Student Identity', stat: 'Not Applicable' },
    { name: 'New Student Residency', stat: 'Not Applicable' },
    {
      name: 'New Student Details ',
      stat:
        infoDashbord?.new_application_steps &&
        infoDashbord?.new_application_steps.student.students_passed,
      details: [
        {
          name: 'Open Flags',
          stat:
            infoDashbord?.new_application_steps &&
            infoDashbord?.new_application_steps.student.flags,
        },
      ],
    },
    { name: 'New Student Income', stat: 'Not Applicable' },
    {
      name: 'New Student Complete',
      stat: infoDashbord?.new_student_completed,
      details: [
        { name: 'In Progress', stat: infoDashbord?.new_student_in_progress },
        { name: 'Withdrawn', stat: infoDashbord?.new_student_withdrawn },
      ],
    },
    {
      name: 'Renewal Applications Started',
      stat: infoDashbord?.renewal_application_started,
    },
    {
      name: 'Renewal Applications Identity ',
      stat:
        infoDashbord?.renewal_application_steps &&
        infoDashbord?.renewal_application_steps.identity.clear +
          infoDashbord?.renewal_application_steps.identity.manual,
    },
    {
      name: 'Residency Renewals Completed ',
      stat:
        infoDashbord?.renewal_application_steps &&
        infoDashbord?.renewal_application_steps.residency.passed,
    },
    { name: 'Renewal Applicant Details', stat: 'Not Applicable' },
    {
      name: 'Renewal Application Income ',
      stat:
        infoDashbord?.renewal_application_steps &&
        infoDashbord?.renewal_application_steps.income.dws +
          infoDashbord.renewal_application_steps.income.tax_commission +
          infoDashbord?.renewal_application_steps.income.manual,
    },
    {
      name: 'Renewal Applications Completed',
      stat: infoDashbord?.renewal_application_completed,
      details: [
        {
          name: 'In Progress',
          stat: infoDashbord?.renewal_application_in_progress,
        },
        {
          name: 'Withdrawn',
          stat: infoDashbord?.renewal_application_withdrawn,
        },
      ],
    },
    {
      name: 'Renewal Students Started',
      stat: infoDashbord?.renewal_student_started,
    },
    { name: 'Renewal Student Identity', stat: 'Not Applicable' },
    { name: 'Renewal Student Residency', stat: 'Not Applicable' },
    {
      name: 'Renewal Student Details ',
      stat:
        infoDashbord?.renewal_application_steps &&
        infoDashbord?.renewal_application_steps.student.students_passed,
      details: [
        {
          name: 'Open Flags',
          stat:
            infoDashbord?.renewal_application_steps &&
            infoDashbord?.renewal_application_steps.student.flags,
        },
      ],
    },
    { name: 'Renewal Student Income', stat: 'Not Applicable' },
    {
      name: 'Renewal Students Completed',
      stat: infoDashbord?.renewal_student_completed,
      details: [
        {
          name: 'In Progress',
          stat: infoDashbord?.renewal_student_in_progress,
        },
        { name: 'Withdrawn', stat: infoDashbord?.renewal_student_withdrawn },
      ],
    },
  ]

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Dashboard',
      }) // Log page view
      hasLoggedPageView.current = true // Mark as executed
    }
  }, [])

  const detailStatColor: Record<number, string> = {
    0: 'red-500',
    1: 'blue-500',
    2: 'gray-200',
  }

  return (
    <form
      id="form"
      noValidate
      className="space-y-3"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex justify-end items-end gap-5">
        <div id="start_date" className="col-span-full 2xl:col-span-3">
          <label
            htmlFor="start_date"
            className="text-sm font-medium leading-6 text-gray-900 my-2"
          >
            Pick Start Date
          </label>
          <Controller
            control={control}
            name="start_date"
            render={({ field }) => (
              <DatePicker
                name={field.name}
                initialDate={ConvertStringToDate(field.value)}
                onDateChange={field.onChange}
                allowFutureDates={true}
              />
            )}
          />
        </div>

        <div id="end_date" className="col-span-full 2xl:col-span-3">
          <label
            htmlFor="end_date"
            className="text-sm font-medium leading-6 text-gray-900 my-2"
          >
            Pick End Date
          </label>
          <Controller
            control={control}
            name="end_date"
            render={({ field }) => (
              <DatePicker
                name={field.name}
                initialDate={ConvertStringToDate(field.value)}
                onDateChange={field.onChange}
                minDate={ConvertStringToDate(start_date)}
                allowFutureDates={true}
              />
            )}
          />
        </div>
        <Button className="mb-1" form="form" type="submit">
          Submit
        </Button>
      </div>
      <FormErrorMessage
        message={errors.end_date?.message}
        className="flex justify-end"
      />
      <div className="flex justify-center pt-8">
        <div className="grid gap-2 md:gap-4 min-[1500px]:gap-8 grid-cols-2 max-[600px]:grid-cols-1 md:grid-cols-3 xl:grid-cols-6">
          {Stats.map((item, index) => (
            <div
              key={index}
              className={`flex flex-col h-auto min-h-[112px] min-[1350px]:min-h-[125px] min-[1500px]:min-h-[150px] rounded-lg bg-white px-2 lg:px-4 xl:px-4 min-[1500px]:py-5 shadow w-[150px] min-[1350px]:w-[175px] min-[1500px]:w-[200px] pb-2`}
            >
              {item.details ? (
                <>
                  {itemStatElement(item.stat)}
                  {itemNameElement(item.name)}
                </>
              ) : (
                <>
                  <div className="mb-8"></div>
                  {itemNameElement(item.name)}
                  {itemStatElement(item.stat)}
                </>
              )}
              {item.details && (
                <div className="mt-1 text-xs">
                  {item.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="flex items-center">
                      <span
                        className={`mr-1 text-${detailStatColor[detailIndex] ?? 'gray-700'} mt-1`}
                        style={{ fontSize: '7px' }}
                      >
                        ●
                      </span>
                      <span className="text-gray-700">{detail.name}</span>
                      <span className="ml-auto text-gray-700">
                        {detail.stat?.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </form>
  )
}

const itemNameElement = (itemName: string) => {
  return (
    <dt className="xl:line-clamp-2 text-sm font-medium text-gray-500">
      {itemName}
    </dt>
  )
}

const itemStatElement = (stat?: string | number) => {
  return (
    <dd
      className={`${typeof stat === 'number' || !isNaN(Number(stat)) ? 'text-2xl' : 'text-xl'} font-semibold tracking-tight text-gray-900`}
    >
      {stat}
    </dd>
  )
}
